<!---->
<script setup>
import { createInput } from "@formkit/vue";
import SignaturePad from "signature_pad";
import { jsPDF } from "jspdf";
import { Canvg } from "canvg";
import segoeUi from "../assets/fonts/Segoe UI.js";
import segoeUiBold from "../assets/fonts/Segoe UI Bold.js";

const smallRadio = createInput(
  {
    $el: "label",
    attrs: {
      class: "$classes.input",
    },
    children: [
      {
        $el: "input",
        attrs: {
          type: "radio",
          class: "hidden peer",
          id: "$radioId",
          onInput: "$handlers.DOMInput",
          value: "$inputValue",
          name: "$inputName",
          icon: "formkit",
          tabindex: "$tabIndex",
        },
      },
      {
        $el: "label",
        attrs: {
          for: "$radioId",
          class:
            "group relative flex w-full h-16 gap-4 items-center p-2 border border-gray-200 peer-checked:border-2 peer-checked:border-brand-green-400 rounded-xl shadow hover:shadow-lg cursor-pointer",
        },
        children: [
          {
            $el: "div",
            attrs: {
              class: "flex items-center p-2 bg-brand-green-100/25 rounded-full",
            },
            children: [
              {
                $el: "svg",
                attrs: {
                  viewBox: "0 0 24 24",
                  width: "24",
                  height: "24",
                  fill: "none",
                  strokeWidth: "2",
                  class: "text-brand-green-400 w-6 h-6",
                  stroke: "currentColor",
                },
                children: [
                  {
                    $el: "path",
                    attrs: {
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      "stroke-width": "2",
                      fill: "",
                      "fill-rule": "",
                      "clip-rule": "",
                      d: "$svg", //svg path
                    },
                  },
                ],
              },
              {
                $el: "svg",
                attrs: {
                  viewBox: "0 0 48 48",
                  width: "48",
                  height: "48",
                  fill: "none",
                  class: "text-brand-green-400 h-10 w-10 absolute",
                  stroke: "currentColor",
                },
                children: [
                  {
                    $el: "path",
                    attrs: {
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      "stroke-width": "2",
                      fill: "currentColor",
                      "fill-rule": "evenodd",
                      "clip-rule": "evenodd",
                      d: "$svg2", //svg path
                    },
                  },
                ],
              },
              {
                $el: "svg",
                attrs: {
                  viewBox: "0 0 48 48",
                  width: "48",
                  height: "48",
                  fill: "none",
                  class: "text-brand-green-400 h-10 w-10 absolute",
                  stroke: "currentColor",
                },
                children: [
                  {
                    $el: "path",
                    attrs: {
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      "stroke-width": "0",
                      fill: "currentColor",
                      "fill-rule": "evenodd",
                      "clip-rule": "evenodd",
                      d: "$svg3", //svg path
                    },
                  },
                ],
              },
            ],
          },
          {
            $el: "span",
            attrs: {
              class: "text-gray-600 font-medium w-48",
            },
            children: "$text",
          },
          {
            $el: "div",
            attrs: {
              class: "absolute right-3",
            },
            children: [
              {
                $el: "div",
                attrs: {
                  class:
                    "group-peer-checked:hidden rounded-full border-2 border-brand-green-500 h-6 w-6",
                },
              },
              {
                $el: "div",
                attrs: {
                  class:
                    "hidden group-peer-checked:block rounded-full bg-brand-green-500 h-6 w-6",
                },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    props: [
      "text",
      "inputValue",
      "radioId",
      "inputName",
      "tabIndex",
      "svg",
      "svg2",
      "svg3",
      "stroke",
      "fill",
    ],
  }
);

const yes = "M4.5 12.75l6 6 9-13.5";

const no = "M6 18L18 6M6 6l12 12";

const clipper =
  "m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13";

const document =
  "M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75";
</script>

<template>
  <div id="main" class="bg-white">
    <div
      v-if="isLoading"
      class="fixed top-0 left-1/2 transform -translate-x-1/2 z-50 mt-4"
    >
      <div>
        <div
          class="w-64 bg-green-500 text-sm text-white rounded-md shadow-lg mb-3 ml-3"
          role="alert"
        >
          <div class="flex p-4">
            <span class="mr-2">PDF wird generiert...</span>

            <div class="ml-auto">
              <button
                type="button"
                class="inline-flex flex-shrink-0 justify-center items-center h-4 w-4 rounded-md text-white/[.5] hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-green-800 focus:ring-green-500 transition-all text-sm dark:focus:ring-offset-green-500 dark:focus:ring-green-700"
              >
                <span class="sr-only">Close</span>
                <svg
                  class="w-3.5 h-3.5"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.92524 0.687069C1.126 0.486219 1.39823 0.373377 1.68209 0.373377C1.96597 0.373377 2.2382 0.486219 2.43894 0.687069L8.10514 6.35813L13.7714 0.687069C13.8701 0.584748 13.9882 0.503105 14.1188 0.446962C14.2494 0.39082 14.3899 0.361248 14.5321 0.360026C14.6742 0.358783 14.8151 0.38589 14.9468 0.439762C15.0782 0.493633 15.1977 0.573197 15.2983 0.673783C15.3987 0.774389 15.4784 0.894026 15.5321 1.02568C15.5859 1.15736 15.6131 1.29845 15.6118 1.44071C15.6105 1.58297 15.5809 1.72357 15.5248 1.85428C15.4688 1.98499 15.3872 2.10324 15.2851 2.20206L9.61883 7.87312L15.2851 13.5441C15.4801 13.7462 15.588 14.0168 15.5854 14.2977C15.5831 14.5787 15.4705 14.8474 15.272 15.046C15.0735 15.2449 14.805 15.3574 14.5244 15.3599C14.2437 15.3623 13.9733 15.2543 13.7714 15.0591L8.10514 9.38812L2.43894 15.0591C2.23704 15.2543 1.96663 15.3623 1.68594 15.3599C1.40526 15.3574 1.13677 15.2449 0.938279 15.046C0.739807 14.8474 0.627232 14.5787 0.624791 14.2977C0.62235 14.0168 0.730236 13.7462 0.92524 13.5441L6.59144 7.87312L0.92524 2.20206C0.724562 2.00115 0.611816 1.72867 0.611816 1.44457C0.611816 1.16047 0.724562 0.887983 0.92524 0.687069Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <FormKit type="form" :actions="false">
      <div class="container mx-auto max-w-screen-lg p-4">
        <h2 class="text-center text-gray-700 font-bold text-2xl">
          Wie lautet ihre Anschrift?
        </h2>
        <div class="flex flex-col md:flex-row gap-x-8 gap-y-6 mt-6">
          <FormKit
            type="text"
            name="AntragstellerName"
            placeholder="Vorname Nachname"
            label="Vorname Nachname"
            validation="required"
            v-model="AntragstellerName"
          />
          <FormKit
            type="text"
            name="AntragstellerStrasseNummer"
            placeholder="Straße Hausnummer"
            label="Straße und Hausnummer"
            validation="required"
            v-model="AntragstellerStrasseNummer"
          />
        </div>
        <div class="flex flex-col md:flex-row gap-x-8 gap-y-6 mt-6">
          <FormKit
            type="number"
            name="AntragstellerPostleitzahl"
            placeholder="PLZ"
            label="Postleitzahl"
            validation="required|numeric|min:10000|max:99999"
            :validation-messages="{
              min: 'Postleitzahl muss fünf-stellig sein',
              max: 'Postleitzahl muss fünf-stellig sein',
            }"
            v-model="AntragstellerPostleitzahl"
          />
          <FormKit
            type="text"
            name="AntragstellerOrt"
            placeholder="Ort"
            label="Ort"
            validation="required"
            v-model="AntragstellerOrt"
          />
        </div>

        <div
          class="mt-20"
          v-if="
            AntragstellerName !== '' &&
            AntragstellerStrasseNummer !== '' &&
            AntragstellerPostleitzahl !== '' &&
            AntragstellerOrt !== ''
          "
        >
          <h2 class="text-center font-bold text-gray-700 text-2xl">
            Wo sind Sie versichert?
          </h2>
          <details class="[&_svg]:open:-rotate-180">
            <summary
              class="flex flex-col justify-center mt-3 cursor-pointer list-none items-center before:content-[''] before:m-0"
            >
              <div class="flex justify-center items-center">
                <div class="mr-1">
                  <svg
                    class="rotate-0 transform text-blue-600 transition-all duration-300"
                    fill="none"
                    height="20"
                    width="20"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
                <div class="text-blue-600 text-md font-semibold">
                  Mehr Informationen
                </div>
              </div>
            </summary>
            <div class="flex justify-center">
              <div
                class="flex bg-gray-50 p-4 rounded-lg mt-2 md:w-[38rem] justify-center"
              >
                <p class="text-gray-600">
                  Bitte wählen Sie Ihre Krankenversicherung aus der Liste aus.
                  In Deutschland werden die Leistungen der Pflegeversicherung
                  von den Krankenkassen verwaltet. Ihre Krankenkasse ist daher
                  Ihr Ansprechpartner für alle Leistungen der
                  Pflegeversicherung.
                </p>
              </div>
            </div>
          </details>
          <div class="flex flex-col md:flex-row gap-x-8 gap-y-6 mt-6">
            <FormKit
              type="select"
              name="KrankenkasseName"
              :options="getKrankenkassenNames"
              placeholder="Krankenkasse wählen"
              validation="required"
              v-model="KrankenkasseName"
            />
          </div>
        </div>

        <div class="mt-20" v-if="KrankenkasseName !== ''">
          <h2
            class="text-center font-bold text-gray-700 text-2xl"
            ref="pflegebedürftigePersonHeading"
          >
            Welche Person möchten Sie pflegen?
          </h2>
          <div class="flex justify-center @md:mx-4 pr-2">
            <details class="[&_svg]:open:-rotate-180">
              <summary
                class="flex flex-col justify-center mt-3 cursor-pointer list-none items-center"
              >
                <div class="flex items-center">
                  <div class="mr-1">
                    <svg
                      class="rotate-0 transform text-blue-600 transition-all duration-300"
                      fill="none"
                      height="20"
                      width="20"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                  <div class="text-blue-600 font-semibold text-md">
                    Mehr Informationen
                  </div>
                </div>
              </summary>
              <div class="flex">
                <div
                  class="flex bg-gray-50 p-4 rounded-lg mt-2 md:w-[38rem] justify-center"
                >
                  <p>
                    Familienpflegezeit kann für die Pflege von Angehörigen bis
                    zum zweiten Verwandtschaftsgrad beantragt werden. Dazu
                    gehören: <br />
                    <b>Eltern</b> - Stiefeltern - Schwiegereltern <br />
                    <b>Kinder</b> - Schwiegerkinder, Enkelkinder, Pflegekinder,
                    Adoptivkinder (auch die des Ehegatten oder
                    Lebenspartners)<br />
                    <b>Ehepartner</b> - Lebensgefährten <br />
                    <b>Kinder</b> - Enkelkinder, Pflegekinder, Adoptivkinder
                    (auch die des Ehegatten oder Lebenspartners) <br />
                  </p>
                </div>
              </div>
            </details>
          </div>
          <div class="flex flex-col md:flex-row gap-x-8 gap-y-6 mt-6">
            <FormKit
              type="text"
              name="PflegebeduerftigePersonName"
              label="Name der pflegebedürftigen Person"
              placeholder="Vorname Nachname"
              validation="required"
              v-model="PflegebeduerftigePersonName"
            />
            <FormKit
              type="select"
              name="Verwandschaftsgrad"
              label="Verwandschaftsgrad"
              placeholder="Bitte wählen"
              validation="required"
              :options="[
                'Eltern',
                'Großeltern',
                'Ehepartner',
                'Geschwister',
                'Kinder',
                'Schwager und Schwägerinnen',
              ]"
              v-model="Verwandschaftsgrad"
            />
            <FormKit
              type="text"
              name="PflegebeduerftigePersonVersicherungsnummer"
              label="Krankenkassenversicerungsnummer"
              placeholder="A123456789"
              validation="required"
              v-model="PflegebeduerftigePersonVersicherungsnummer"
            />
          </div>
        </div>

        <div
          class="mt-20"
          v-if="PflegebeduerftigePersonName !== '' && Verwandschaftsgrad !== ''"
        >
          <div class="flex flex-col pl-2 md:pl-0">
            <h2
              class="text-center font-bold text-gray-700 text-2xl"
              ref="letzteFreistellungHeading"
            >
              Der Nachweis über den Pflegegrad von
              {{ PflegebeduerftigePersonName }}...
            </h2>
            <div class="flex flex-col md:flex-row gap-x-8 gap-y-6 mt-6">
              <FormKit
                :type="smallRadio"
                :svg="clipper"
                validation="required"
                name="NachweisPflegegrad"
                text="ist beigefügt."
                inputValue="ist beigefügt"
                inputName="NachweisPflegegrad"
                radioId="2"
                v-model="NachweisPflegegrad"
              />
              <FormKit
                :type="smallRadio"
                :svg="document"
                validation="required"
                name="NachweisPflegegrad"
                text="liegt der Firma vor."
                inputValue="liegt vor, da ich bereits eine Freistellung in Anspruch genommen habe"
                inputName="NachweisPflegegrad"
                radioId="3"
                v-model="NachweisPflegegrad"
              />
            </div>
          </div>
        </div>

        <div
          class="mt-20"
          v-if="
            NachweisPflegegrad ===
            'liegt vor, da ich bereits eine Freistellung in Anspruch genommen habe'
          "
        >
          <h2 class="text-center font-bold text-gray-700 text-2xl">
            Wann waren Sie zuletzt freigestellt?
          </h2>
          <div class="flex flex-col md:flex-row gap-x-8 gap-y-6 mt-6">
            <FormKit
              type="date"
              value="2023-01-01"
              label="Startdatum"
              help="von"
              validation="required"
              name="ZeitraumVorherigeFreistellungStartDatum"
              v-model="ZeitraumVorherigeFreistellungStartDatum"
            />
            <FormKit
              type="date"
              value="2023-01-01"
              label="Enddatum"
              help="bis"
              :validation="[
                ['date_after', ZeitraumVorherigeFreistellungStartDatum],
              ]"
              v-model="ZeitraumVorherigeFreistellungEndDatum"
            />
          </div>
        </div>

        <div
          class="mt-20"
          v-if="
            (NachweisPflegegrad ===
              'liegt vor, da ich bereits eine Freistellung in Anspruch genommen habe' &&
              ZeitraumVorherigeFreistellungStartDatum !== '' &&
              ZeitraumVorherigeFreistellungEndDatum !== '') ||
            NachweisPflegegrad === 'ist beigefügt'
          "
        >
          <h2
            class="text-center font-bold text-gray-700 text-2xl"
            ref="arbeitgeberHeading"
          >
            Wie lautet die Adresse ihres Arbeitgebers?
          </h2>
          <div class="flex flex-col md:flex-row gap-x-8 gap-y-6 mt-6">
            <FormKit
              type="text"
              name="ArbeitgeberName"
              label="Name des Arbeitgebers"
              placeholder="Firma"
              validation="required"
              v-model="ArbeitgeberName"
            />
            <FormKit
              type="text"
              name="ArbeitgeberStrasseNummer"
              label="Straße und Hausnummer"
              placeholder="Straße Hausnummer"
              validation="required"
              v-model="ArbeitgeberStrasseNummer"
            />
          </div>
          <div class="flex flex-col md:flex-row gap-x-8 gap-y-6 mt-6">
            <FormKit
              type="number"
              name="ArbeitgeberPostleitzahl"
              label="Postleitzahl"
              placeholder="PLZ"
              validation="required|numeric|min:10000|max:99999"
              :validation-messages="{
                min: 'Postleitzahl muss fünf-stellig sein',
                max: 'Postleitzahl muss fünf-stellig sein',
              }"
              v-model="ArbeitgeberPostleitzahl"
            />
            <FormKit
              type="text"
              name="ArbeitgeberOrt"
              label="Ort"
              placeholder="Ort"
              validation="required"
              v-model="ArbeitgeberOrt"
            />
          </div>
        </div>

        <div
          class="mt-20"
          v-if="
            ArbeitgeberName !== '' &&
            ArbeitgeberStrasseNummer !== '' &&
            ArbeitgeberPostleitzahl !== '' &&
            ArbeitgeberOrt !== ''
          "
        >
          <h2 class="text-center font-bold text-gray-700 text-2xl">
            Für welchen Zeitraum möchten Sie die Pflegezeit beantragen?
          </h2>
          <div class="flex flex-col md:flex-row gap-x-8 gap-y-6 mt-6">
            <FormKit
              type="date"
              value="2011-01-01"
              label="Startdatum"
              help="von"
              validation="required"
              v-model="AuszeitStartDatum"
            />
            <FormKit
              type="date"
              value="2011-01-01"
              label="Enddatum"
              help="bis"
              :validation="[['date_after', AuszeitStartDatum]]"
              v-model="AuszeitEndDatum"
            />
          </div>
        </div>

        <div
          class="mt-20"
          v-if="AuszeitStartDatum !== '' && AuszeitEndDatum !== ''"
        >
          <h2 class="text-center font-bold text-gray-700 text-2xl">
            Wie viele Stunden arbeiten Sie aktuell pro Woche und auf wie viele
            Stunden möchten Sie reduzieren?
          </h2>
          <div class="flex flex-col md:flex-row gap-x-8 gap-y-6 mt-6">
            <FormKit
              v-model="AktuelleArbeitszeitProWoche"
              type="number"
              label="Aktuelle Arbeitszeit pro Woche"
              help="in Stunden"
              placeholder="40"
              validation="required|numeric|min:1|max:120"
            />
            <FormKit
              v-model="ReduzierteArbeitszeitProWoche"
              type="number"
              label="Reduzierte Artbeitszeit pro Woche"
              help="in Stunden"
              placeholder="20"
              validation="required|min:1|max:120"
            />
          </div>
        </div>

        <div class="mt-20" v-if="ReduzierteArbeitszeitProWoche !== ''">
          <h2 class="text-center font-bold text-gray-700 text-2xl">
            Wie wollen Sie die Arbeitszeit aufteilen?
          </h2>
          <div class="flex justify-center @md:mx-4 pr-2">
            <details class="[&_svg]:open:-rotate-180">
              <summary
                class="flex flex-col justify-center mt-3 cursor-pointer list-none items-center"
              >
                <div class="flex items-center">
                  <div class="mr-1">
                    <svg
                      class="rotate-0 transform text-blue-600 transition-all duration-300"
                      fill="none"
                      height="20"
                      width="20"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                  <div class="text-blue-600 font-semibold text-md">
                    Mehr Informationen
                  </div>
                </div>
              </summary>
              <div class="flex flex-col">
                <div
                  class="flex bg-gray-50 p-4 rounded-lg mt-2 md:w-[38rem] justify-center"
                >
                  <p>
                    Hier geben Sie an wie viele Stunden Sie zukünftig an wie
                    vielen Tagen arbeiten möchten. Zum Beispiel 5 Tage die Woche
                    a 4 Stunden ergeben 20 Stunden pro Woche.
                  </p>
                </div>
              </div>
            </details>
          </div>
          <div class="flex flex-col md:flex-row gap-x-8 gap-y-6 mt-6">
            <FormKit
              v-model="ArbeitszeitVerteilungTage"
              type="number"
              label="Tage"
              placeholder="5"
              validation="required|numeric|min:1|max:7"
            />
            <FormKit
              v-model="ArbeitszeitVerteilungStunden"
              type="number"
              label="Stunden"
              placeholder="4"
              validation="required|numeric|min:1|max:10"
            />
          </div>
        </div>

        <div class="mt-20" v-if="ArbeitszeitVerteilungStunden !== ''">
          <h2
            class="text-center font-bold text-gray-700 text-2xl"
            ref="DigitalUnterschreibenHeading"
          >
            Möchten Sie das Formular digital unterschreiben?
          </h2>
          <div class="flex justify-center @md:mx-4 pr-2">
            <details class="[&_svg]:open:-rotate-180">
              <summary
                class="flex flex-col justify-center mt-3 cursor-pointer list-none items-center before:content-[''] before:m-0"
              >
                <div class="flex items-center">
                  <div class="mr-1">
                    <svg
                      class="rotate-0 transform text-blue-600 transition-all duration-300"
                      fill="none"
                      height="20"
                      width="20"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                  <div class="text-blue-600 font-semibold text-md">
                    Mehr Informationen
                  </div>
                </div>
              </summary>
              <div class="flex flex-col">
                <div
                  class="flex bg-gray-50 p-4 rounded-lg mt-2 md:w-[38rem] justify-center"
                >
                  <p class="text-gray-600">
                    Wenn Sie 'Ja' wählen, steht Ihnen die Möglichkeit zur
                    Verfügung, das Dokument einfach digital zu unterschreiben
                    und es dann bequem per E-Mail zu versenden. Wenn Sie 'Nein'
                    wählen müssen Sie handschriftlich unterschreiben.
                  </p>
                </div>
              </div>
            </details>
          </div>
          <div class="flex flex-col md:flex-row gap-x-8 gap-y-6 mt-6">
            <FormKit
              :type="smallRadio"
              :svg="yes"
              validation="required"
              name="DigitalUnterschreiben"
              text="Ja"
              inputValue="Ja"
              inputName="DigitalUnterschreiben"
              radioId="5523"
              v-model="DigitalUnterschreiben"
            />
            <FormKit
              :type="smallRadio"
              :svg="no"
              validation="required"
              name="DigitalUnterschreiben"
              text="Nein"
              inputValue="Nein"
              inputName="DigitalUnterschreiben"
              radioId="6236"
              v-model="DigitalUnterschreiben"
            />
          </div>
        </div>

        <div
          class="flex flex-col mt-20"
          :class="{
            hidden: !(DigitalUnterschreiben === 'Ja'),
          }"
        >
          <h2
            class="text-center font-bold text-gray-700 text-2xl"
            ref="unterschriftHeading"
          >
            Unterschrift von {{ VollmachtGeberName }}
          </h2>
          <div class="flex justify-center">
            <h3 class="text-lg text-gray-600 text-center max-w-xl mt-2 p-4">
              Nutzen Sie Ihre Maus, Trackpad oder Finger, um Ihre Unterschrift
              in das gelb umrandete Feld zu zeichnen.
            </h3>
          </div>
          <div class="flex justify-center">
            <div class="w-[50rem] h-full p-4">
              <div class="relative z-0 col-span-2 h-64 w-full">
                <canvas
                  ref="signing_canvas"
                  class="w-full h-full bg-white border-4 border-brand-yellow-400 rounded-lg"
                ></canvas>
                <div class="absolute bottom-6 right-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-12 h-12 text-gray-200"
                    width="44"
                    height="44"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M8 13v-8.5a1.5 1.5 0 0 1 3 0v7.5" />
                    <path d="M11 11.5v-2a1.5 1.5 0 1 1 3 0v2.5" />
                    <path d="M14 10.5a1.5 1.5 0 0 1 3 0v1.5" />
                    <path
                      d="M17 11.5a1.5 1.5 0 0 1 3 0v4.5a6 6 0 0 1 -6 6h-2h.208a6 6 0 0 1 -5.012 -2.7a69.74 69.74 0 0 1 -.196 -.3c-.312 -.479 -1.407 -2.388 -3.286 -5.728a1.5 1.5 0 0 1 .536 -2.022a1.867 1.867 0 0 1 2.28 .28l1.47 1.47"
                    />
                  </svg>
                </div>
              </div>
              <div class="flex justify-between items-center col-span-2 mt-4">
                <button
                  class="flex content-center px-2 py-1 border-2 border-rose-400 rounded-md"
                  @click="clearSignature()"
                  type="button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="mt-1 h-4 w-4 stroke-current text-rose-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  <span class="ml-2 text-rose-500">neuer Versuch</span>
                </button>
                <button
                  class="flex content-center py-1 px-4 text-lg rounded outline-none ring-2 ring-brand-green-300 bg-brand-green-50 focus:outline-none"
                  type="button"
                  @click="hasSignature()"
                >
                  <span class="text-brand-green-600"
                    >Unterschrift verwenden</span
                  >
                </button>
              </div>
              <div class="text-right mt-2">
                <div
                  class="inline-flex justify-end mt-2 text-rose-600 text-md border rounded-md truncate mb-1 text-md p-2 shadow-lg bg-rose-50 border-rose-300"
                  v-if="AlertUnterschrift"
                >
                  Es muss eine Unterschrift vorhanden sein.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="mt-20"
          v-if="UnterschriftVorhanden || DigitalUnterschreiben == 'Nein'"
        >
          <h2
            class="text-center font-bold text-gray-700 text-2xl"
            ref="adresseKrankenkasseHeading"
          >
            Adresse der Pflegekasse {{ KrankenkasseName }}
          </h2>
          <div class="flex justify-center">
            <h3 class="text-center text-gray-600 text-lg my-2 max-w-3xl">
              Sie haben es geschafft! Das Formular steht jetzt zum Download
              bereit. Sie können es bequem per E-Mail an Ihre Krankenkasse
              senden oder ausdrucken und per Post schicken.
            </h3>
          </div>
          <div
            class="flex flex-col md:flex-row @md:px-0 justify-center items-center gap-x-8"
          >
            <div class="mt-2">
              <main class="h-full overflow-y-auto">
                <div class="container mx-auto grid">
                  <!-- Card -->
                  <div
                    class="flex items-center p-4 bg-white rounded-lg shadow-xs"
                  >
                    <div
                      class="p-3 mr-4 text-brand-green-400 bg-brand-green-100/25 rounded-full"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        class="w-6 h-6"
                      >
                        <path
                          d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z"
                        />
                        <path
                          d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z"
                        />
                      </svg>
                    </div>
                    <div>
                      <div class="text-md text-gray-600">
                        <b>Adresse:</b> {{ getKrankenkasseAddress }}
                        <br />
                        <b>E-Mail: </b>
                        <a
                          :href="'mailto:' + getSelectedKrankenkasseEmail"
                          class="border-b-2 font-medium border-yellow-500"
                        >
                          {{ getSelectedKrankenkasseEmail }}
                        </a>
                      </div>
                    </div>
                  </div>

                  <div
                    class="flex items-center p-4 rounded-lg shadow-xs"
                    v-if="NachweisPflegegrad === 'ist beigefügt'"
                  >
                    <div
                      class="p-3 mr-4 text-rose-400 bg-rose-100/25 rounded-full"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        class="w-6 h-6"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M7.502 6h7.128A3.375 3.375 0 0118 9.375v9.375a3 3 0 003-3V6.108c0-1.505-1.125-2.811-2.664-2.94a48.972 48.972 0 00-.673-.05A3 3 0 0015 1.5h-1.5a3 3 0 00-2.663 1.618c-.225.015-.45.032-.673.05C8.662 3.295 7.554 4.542 7.502 6zM13.5 3A1.5 1.5 0 0012 4.5h4.5A1.5 1.5 0 0015 3h-1.5z"
                          clip-rule="evenodd"
                        />
                        <path
                          fill-rule="evenodd"
                          d="M3 9.375C3 8.339 3.84 7.5 4.875 7.5h9.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-9.75A1.875 1.875 0 013 20.625V9.375zm9.586 4.594a.75.75 0 00-1.172-.938l-2.476 3.096-.908-.907a.75.75 0 00-1.06 1.06l1.5 1.5a.75.75 0 001.116-.062l3-3.75z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </div>
                    <div>
                      <div class="text-md text-gray-600">
                        <p class="text-rose-600 text-md">
                          Vergessen Sie nicht, den Nachweis über den Pflegegrad
                          von {{ PflegebeduerftigePersonName }} beizufügen.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </main>
            </div>
            <div class="flex justify-center p-4 gap-x-8">
              <button
                @click="generatePDF()"
                type="button"
                data-analytics='"clickDownload", {"props":{"page":"{{craft.app.request.getUrl()}}"}}'
                :disabled="isLoading"
                class="p-2 w-56 text-lg text-brand-green-500 font-medium rounded shadow-md shadow-brand-green-300 outline-none active:shadow-md bg-gradient-to-br from-brand-green-300 hover:from-brand-green-400 to-brand-green-400 hover:to-brand-green-500 ring-2 ring-white focus:outline-none"
              >
                <div class="flex gap-x-4 items-center">
                  <div class="p-2 bg-brand-green-100 rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      class="w-6 h-6"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M5.625 1.5H9a3.75 3.75 0 013.75 3.75v1.875c0 1.036.84 1.875 1.875 1.875H16.5a3.75 3.75 0 013.75 3.75v7.875c0 1.035-.84 1.875-1.875 1.875H5.625a1.875 1.875 0 01-1.875-1.875V3.375c0-1.036.84-1.875 1.875-1.875zm5.845 17.03a.75.75 0 001.06 0l3-3a.75.75 0 10-1.06-1.06l-1.72 1.72V12a.75.75 0 00-1.5 0v4.19l-1.72-1.72a.75.75 0 00-1.06 1.06l3 3z"
                        clip-rule="evenodd"
                      />
                      <path
                        d="M14.25 5.25a5.23 5.23 0 00-1.279-3.434 9.768 9.768 0 016.963 6.963A5.23 5.23 0 0016.5 7.5h-1.875a.375.375 0 01-.375-.375V5.25z"
                      />
                    </svg>
                  </div>
                  <span v-if="isLoading" class="flex ml-9 text-gray-100">
                    <LoadingSpinner />
                  </span>
                  <span v-else class="text-gray-100"> Herunterladen </span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </FormKit>
  </div>
</template>
<style>
details[open] summary {
  text-align: left;
}
</style>

<script>
import LoadingSpinner from "./LoadingSpinner.vue";

export default {
  name: "FormularView",
  components: {
    LoadingSpinner,
  },

  computed: {
    getKrankenkassenNames() {
      return this.pflegekassen.map((kasse) => kasse.Name);
    },
    selectedKrankenkasse() {
      return (
        this.pflegekassen.find(
          (kasse) => kasse.Name === this.KrankenkasseName
        ) || {}
      );
    },
    getKrankenkasseAddress() {
      return this.selectedKrankenkasse.Adresse || "";
    },
    getSelectedKrankenkasseEmail() {
      return this.selectedKrankenkasse["E-Mail"] || "";
    },
  },

  methods: {
    krankenKassenAdresseToString() {
      this.PflegeversicherungAdresse = this.getKrankenkasseAddress.toString();
    },

    hasSignature() {
      if (this.signaturepad.isEmpty()) {
        this.UnterschriftVorhanden = false;
        this.AlertUnterschrift = true;
      } else {
        this.UnterschriftVorhanden = true;
        this.AlertUnterschrift = false;
        let targetRef;

        targetRef = "adresseKrankenkasseHeading";

        this.scrollToElement(targetRef);
      }
    },

    //changes Date from US to Eu format and captures the signature
    updateDateAndCaptureSignature() {
      this.captureSignature();
    },

    //checks whos the Antragsteller
    AntragForWho() {
      if (this.Antragsteller === "FuerMich") {
        this.fuerWen = "Für mich selbst";
      } else if (this.Antragsteller === "FuerJemand") {
        this.fuerWen = "ich als bevollmächtigte/r";
      }
    },

    //checks if a Vollmacht is vorhanden
    VollmachtLiegtVorFillPdf() {
      if (this.VollmachtLiegtVor == "Ja") {
        this.VollmachtFillPdf = "Ja, liegt in Kopie bei";
      } else {
        this.VollmachtFillPdf = "";
      }
    },

    //changes the date format from US to EU
    changeDateFormat() {
      if (this.PflegeversicherungBescheidDatum) {
        let parts = this.PflegeversicherungBescheidDatum.split("-");
        this.PflegeversicherungBescheidDatumEU = `${parts[2]}.${parts[1]}.${parts[0]}`;
      }

      if (this.AuszeitStartDatum) {
        let parts = this.AuszeitStartDatum.split("-");
        this.AuszeitStartDatumEU = `${parts[2]}.${parts[1]}.${parts[0]}`;
      }

      if (this.AuszeitEndDatum) {
        let parts = this.AuszeitEndDatum.split("-");
        this.AuszeitEndDatumEU = `${parts[2]}.${parts[1]}.${parts[0]}`;
      }
    },

    async generatePDF() {
      var pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        putOnlyUsedFonts: true,
        compress: true,
        floatPrecision: 16, // or "smart", default is 16
      });

      //setzt Loading state auf true
      this.isLoading = true;

      this.updateDateAndCaptureSignature();
      this.concatAbsenderInfos();
      this.changeFormatOfKrankenkasseAdresse();
      this.krankenKassenAdresseToString();
      this.changeDateFormat();

      let svgTextWiderspruchPflegegrad = null;

      try {
        // Fetch the SVG files
        const responseWiderspruchPflegegrad = await fetch(
          //for development "ankuendigung-familienpflegezeit.svg"
          "/assets/vue-forms/ankuendigung-familienpflegezeit/dist/ankuendigung-familienpflegezeit.svg"
        );

        if (!responseWiderspruchPflegegrad.ok) {
          throw new Error("SVG file not found");
        }

        svgTextWiderspruchPflegegrad =
          await responseWiderspruchPflegegrad.text();
      } catch (error) {
        console.error(error);
      }

      // Convert the SVGs to canvases
      const WiderspruchPflegegrad = document.createElement("canvas");
      WiderspruchPflegegrad.width = this.mmToPx(210);
      WiderspruchPflegegrad.height = this.mmToPx(297);
      const ctxWiderspruchPflegegrad = WiderspruchPflegegrad.getContext("2d");
      const vWiderspruchPflegegrad = await Canvg.fromString(
        ctxWiderspruchPflegegrad,
        svgTextWiderspruchPflegegrad
      );
      vWiderspruchPflegegrad.start();

      // Convert the canvases to image data URLs
      const imgDataWiderspruchPflegegrad =
        WiderspruchPflegegrad.toDataURL("image/png");

      // Add the images to the PDF
      pdf.addImage(imgDataWiderspruchPflegegrad, "PNG", 0, 0, 210, 297);

      pdf.addFileToVFS("Segoe UI.ttf", segoeUi);
      pdf.addFont("Segoe UI.ttf", "Segoe UI", "normal");

      pdf.addFileToVFS("Segoe UI Bold.ttf", segoeUiBold);
      pdf.addFont("Segoe UI Bold.ttf", "Segoe UI", "bold");

      // Set the font
      pdf.setFont("Segoe UI");
      pdf.setFontSize("8");

      // Absender Briefkopf
      pdf.text(this.Absender, 24, 41);

      //Empfänger Briefkopf
      pdf.setFontSize("12");
      pdf.text(this.ArbeitgeberName, 24, 51);
      pdf.text(this.ArbeitgeberStrasseNummer, 24, 58);
      pdf.text(this.ArbeitgeberPostleitzahl, 24, 65);
      pdf.text(this.ArbeitgeberOrt, 24, 72);

      //Absender Daten (links oben)
      pdf.setFontSize("10");
      pdf.text(this.AntragstellerName, 125, 51);
      pdf.text(this.AntragstellerStrasseNummer, 125, 57);
      pdf.text(this.AntragstellerPostleitzahl, 125, 64);
      pdf.text(this.AntragstellerOrt, 125, 71);

      pdf.setTextColor(38, 38, 38);

      pdf.text(this.PflegebeduerftigePersonVersicherungsnummer, 61, 146);

      pdf.text(this.PflegebeduerftigePersonName, 37, 152.4);

      pdf.text(this.Verwandschaftsgrad, 60, 158.8);

      pdf.text(this.KrankenkasseName, 119, 146);

      pdf.text(this.PflegeversicherungAdresse, 124, 152.4);

      if (this.NachweisPflegegrad === "ist beigefügt") {
        pdf.text("x", 19.5, 170.6);
      } else if (
        this.NachweisPflegegrad ===
        "liegt vor, da ich bereits eine Freistellung in Anspruch genommen habe"
      ) {
        pdf.text("x", 19.5, 177);
        pdf.text(this.AuszeitStartDatumEU, 33, 184.1);

        pdf.text(this.AuszeitEndDatumEU, 56.4, 184.1);
      }

      pdf.text(this.AktuelleArbeitszeitProWoche, 42.4, 203.4);

      pdf.text(this.ReduzierteArbeitszeitProWoche, 54, 203.4);

      pdf.text(this.ArbeitszeitVerteilungTage, 25, 209.5);
      pdf.text(this.ArbeitszeitVerteilungStunden, 54.7, 209.7);

      if (this.DigitalUnterschreiben === "Ja") {
        this.captureSignature();
        pdf.addImage(this.Unterschrift, "PNG", 24, 231, 40, 10);
      }

      pdf.text(this.AntragstellerName, 25, 246);

      pdf.setFont("Segoe UI", "bold");

      pdf.save("ankuendigung-familienpflegezeit.pdf");

      //setzt loading state auf false
      this.isLoading = false;
    },

    mmToPx(mm) {
      var dpi = 300; // Standard DPI for web browsers
      return Math.floor((mm * dpi) / 25.4);
    },

    concatAbsenderInfos() {
      this.Absender =
        this.AntragstellerName +
        ", " +
        this.AntragstellerStrasseNummer +
        ", " +
        this.AntragstellerPostleitzahl +
        ", " +
        this.AntragstellerOrt;
    },

    changeFormatOfKrankenkasseAdresse() {
      let krankenkasseadresse = this.getKrankenkasseAddress.toString();
      let parts = krankenkasseadresse.split(",");
      this.KrankenkasseStrasseNummer = parts[0];
      this.KrankenkassePlzOrt = parts.slice(1).join(",");

      //delete whitespaces
      this.KrankenkassePlzOrt = this.KrankenkassePlzOrt.replace(/\s/g, "");
    },
    evaluateAndShowSignatureSection() {
      // Überprüfen Sie hier die Bedingungen und setzen Sie showSignatureSection entsprechend
      this.showSignatureSection =
        (this.PflegebeduerftigePersonName !== "" &&
          this.PflegebeduerftigePersonStrasseNummer !== "" &&
          this.PflegebeduerftigePersonPostleitzahl !== "" &&
          this.PflegebeduerftigePersonOrt !== "" &&
          this.PflegebeduerftigePersonVersicherungsnummer !== "") ||
        (this.PflegeversicherungBescheidDatum !== "" &&
          this.PflegeversicherungName !== "");
      // Rufen Sie Ihre Methode auf, wenn showSignatureSection auf true gesetzt wird
      if (this.showSignatureSection) {
        this.resizeCanvas();
      }
    },

    //captures the signature and saves it as a base64 encoded string
    captureSignature() {
      this.Unterschrift = this.signaturepad.toDataURL("image/png");
    },

    //clears the signature pad
    clearSignature() {
      this.resizeCanvas();
      this.UnterschriftVorhanden = false;
      this.signaturepad.clear();
    },

    resizeCanvas() {
      var canvas = this.$refs.signing_canvas;
      var ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d").scale(ratio, ratio);
    },
    //scrolls to the element with the according res
    scrollToElement(ref) {
      this.$nextTick(() => {
        const offset = 90;

        const el = this.$refs[ref];
        if (el) {
          // Get the position of the element
          const rect = el.getBoundingClientRect();
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;

          // Scroll to the element
          window.scrollTo({
            top: rect.top + scrollTop - offset,
            behavior: "smooth",
          });
        }
      });
    },
  },

  watch: {
    //resize signature pad when date is changed
    ArbeitszeitVerteilungTage() {
      this.$nextTick(() => {
        this.resizeCanvas();
      });
      this.resizeCanvas();
    },

    ArbeitszeitVerteilungStunden() {
      this.$nextTick(() => {
        this.resizeCanvas();
      });
      this.resizeCanvas();
    },

    KrankenkasseName(newVal, oldVal) {
      if (newVal === oldVal) return;

      let targetRef = "pflegebedürftigePersonHeading";

      this.scrollToElement(targetRef);
    },

    NachweisPflegegrad(newVal, oldVal) {
      if (newVal === oldVal) return;

      let targetRef;

      if (this.NachweisPflegegrad == "ist beigefügt") {
        targetRef = "arbeitgeberHeading";
      } else {
        targetRef = "letzteFreistellungHeading";
      }

      this.scrollToElement(targetRef);
    },

    DigitalUnterschreiben(newVal, oldVal) {
      this.$nextTick(() => {
        this.resizeCanvas();
      });
      this.resizeCanvas();

      if (newVal === oldVal) return;

      if (newVal === "Ja") {
        let targetRef;

        targetRef = "unterschriftHeading";

        this.scrollToElement(targetRef);
      } else {
        let targetRef;

        targetRef = "adresseKrankenkasseHeading";

        this.scrollToElement(targetRef);
      }
    },
  },

  mounted() {
    //initialize signature pad
    this.signaturepad = new SignaturePad(this.$refs.signing_canvas);
    //resize signature pad
    this.resizeCanvas();
  },

  data() {
    return {
      AlertUnterschrift: false,
      UnterschriftVorhanden: false,
      DigitalUnterschreiben: "",

      isLoading: false,
      AntragstellerName: "",
      AntragstellerStrasseNummer: "",
      AntragstellerPostleitzahl: "",
      AntragstellerOrt: "",
      KrankenkasseName: "",
      AntragstellerVersicherungsnummer: "",
      PflegebeduerftigePersonName: "",
      PflegebeduerftigePersonVersicherungsnummer: "",
      Verwandschaftsgrad: "",
      NachweisPflegegrad: "",
      AuszeitStartDatum: "",
      AuszeitEndDatum: "",
      AuszeitStartDatumEU: "",
      AuszeitEndDatumEU: "",
      ArbeitgeberName: "",
      ArbeitgeberStrasseNummer: "",
      ArbeitgeberPostleitzahl: "",
      ArbeitgeberOrt: "",
      MöchteWeiterarbeiten: "",
      ZeitraumVorherigeFreistellungStartDatum: "",
      ZeitraumVorherigeFreistellungEndDatum: "",
      AktuelleArbeitszeitProWoche: "",
      ReduzierteArbeitszeitProWoche: "",
      ArbeitszeitVerteilungTage: "",
      ArbeitszeitVerteilungStunden: "",
      ZustimmungAgbDatenschutz: "",
      Unterschrift: "",
      currentDate: new Date().toLocaleDateString(),
      MecasaLogo: "",
      Absender: "",
      jsonData: null,
      pflegekassen: [
        {
          Name: "AOK Niedersachsen",
          Bundesland: "Niedersachsen",
          Adresse: "Hildesheimer Straße 273, 30519 Hannover",
          "E-Mail": "AOK.Service@nds.aok.de",
        },
        {
          Name: "AOK Baden-Württemberg",
          Bundesland: "Baden-Württemberg",
          Adresse: "Presselstraße 19, 70191 Stuttgart",
          "E-Mail": "info@bw.aok.de",
        },
        {
          Name: "AOK Bayern",
          Bundesland: "Bayern",
          Adresse: "Carl-Wery-Straße 28, 81739 München",
          "E-Mail": "info@by.aok.de",
        },
        {
          Name: "AOK Bremen/Bremerhaven",
          Bundesland: "Bremen, Bremerhaven",
          Adresse: "Bürgermeister-Smidt-Straße 95, 28195 Bremen",
          "E-Mail": "info@hb.aok.de",
        },
        {
          Name: "AOK Hessen",
          Bundesland: "Hessen",
          Adresse: "Basler Straße 2, 61352 Bad Homburg v.d.H.",
          "E-Mail": "service@he.aok.de",
        },
        {
          Name: "AOK Nordost",
          Bundesland: "Mecklenburg-Vorpommern, Brandenburg, Berlin",
          Adresse: "Brandenburger Straße 72, 14467 Potsdam",
          "E-Mail": "service@nordost.aok.de",
        },
        {
          Name: "AOK NordWest",
          Bundesland: "Schleswig-Holstein, Nordrhein-Westfalen",
          Adresse: "Kopenhagener Straße 1, 44269 Dortmund",
          "E-Mail": "https://www.aok.de/pk/kontaktformular",
        },
        {
          Name: "AOK Rheinland/Hamburg",
          Bundesland: "Hamburg, Nordrhein-Westphalen",
          Adresse: "Kasernenstraße 61, 40213 Düsseldorf",
          "E-Mail": "aok@rh.aok.de",
        },
        {
          Name: "AOK Rheinland-Pfalz/Saarland",
          Bundesland: "Rheinland-Pfalz, Saarland",
          Adresse: "Virchowstraße 30, 67304 Eisenberg",
          "E-Mail": "service@rps.aok.de",
        },
        {
          Name: "AOK Sachsen-Anhalt",
          Bundesland: "Sachsen-Anhalt",
          Adresse: "Lüneburger Straße 4, 39106 Magdeburg",
          "E-Mail": "service@san.aok.de",
        },
        {
          Name: "AOK PLUS",
          Bundesland: "Sachsen, Thüringen",
          Adresse: "Sternplatz 7, 01067 Dresden",
          "E-Mail": "service@plus.aok.de",
        },
        {
          Name: "Audi BKK",
          Bundesland: "Bundesweit",
          Adresse: "Ferdinand-Braun-Straße 6, 85053 Ingolstadt",
          "E-Mail": "info@audibkk.de",
        },
        {
          Name: "BAHN-BKK",
          Bundesland: "Bundesweit",
          Adresse: "Franklinstraße 54, 60486 Frankfurt am Main",
          "E-Mail": "service@bahn-bkk.de",
        },
        {
          Name: "BARMER",
          Bundesland: "Bundesweit",
          Adresse: "Axel-Springer-Straße 44, 10969 Berlin",
          "E-Mail": "service@barmer.de",
        },
        {
          Name: "BERGISCHE Krankenkasse",
          Bundesland: "Hamburg, Hessen, Nordrhein-Westfalen",
          Adresse: "Heresbachstraße 29, 42719 Solingen",
          "E-Mail": "info@bergische-krankenkasse.de",
        },
        {
          Name: "Bertelsmann BKK",
          Bundesland:
            "Baden-Württemberg, Bayern, Berlin, Hamburg, Hessen, Niedersachsen, Nordrhein-Westfalen, Sachsen, Thüringen",
          Adresse: "Carl-Miele-Str. 214, 33311 Gütersloh",
          "E-Mail": "service@bertelsmann-bkk.de",
        },
        {
          Name: "Betriebskrankenkasse Mobil",
          Bundesland: "Bundesweit",
          Adresse: "Friedenheimer Brücke 29, 80639 München",
          "E-Mail": "info@service.mobil-krankenkasse.de",
        },
        {
          Name: "BKK PricewaterhouseCoopers",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Burgstraße 1 - 3, 34212 Melsungen",
          "E-Mail": "poststelle@bas.bund.de",
        },
        {
          Name: "BIG direkt gesund",
          Bundesland: "Bundesweit",
          Adresse: "Rheinische Straße 1, 44137 Dortmund",
          "E-Mail": "info@big-direkt.de",
        },
        {
          Name: "BKK Akzo Nobel Bayern",
          Bundesland: "Bayern",
          Adresse: "Glanzstoffstraße 1, 63906 Erlenbach",
          "E-Mail": "versicherungen@bkk-akzo.de",
        },
        {
          Name: "BKK B. Braun Aesculap",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Grüne Straße 1, 34212 Melsungen",
          "E-Mail": "info@bkk-bba.de",
        },
        {
          Name: "BKK BPW Bergische Achsen KG",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Ohler Berg 1, 51674 Wiehl",
          "E-Mail": "info@bkk-bpw.de",
        },
        {
          Name: "BKK Deutsche Bank AG",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Königsallee 60c, 40212 Düsseldorf",
          "E-Mail": "bkkdb.info@bkkdb.de",
        },
        {
          Name: "BKK Diakonie",
          Bundesland:
            "Baden-Württemberg, Bayern, Berlin, Brandenburg, Bremen, Hamburg, Hessen, Mecklenburg-Vorpommern, Niedersachsen, Nordrhein-Westfalen, Rheinland-Pfalz, Sachsen, Sachsen-Anhalt, Schleswig-Holstein",
          Adresse: "Königsweg 8, 33617 Bielefeld",
          "E-Mail": "info@bkk-diakonie.de",
        },
        {
          Name: "BKK Euregio",
          Bundesland: "Hamburg, Nordrhein-Westfalen",
          Adresse: "Boos-Fremery-Str. 66, 52525 Heinsberg",
          "E-Mail": "info@bkk-euregio.de",
        },
        {
          Name: "BKK EVM",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Schützenstraße 80-82, 56068 Koblenz",
          "E-Mail": "info@bkk-evm.de",
        },
        {
          Name: "BKK EWE",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Staulinie 16–17, 26122 Oldenburg",
          "E-Mail": "info@bkk-ewe.de",
        },
        {
          Name: "BKK exklusiv",
          Bundesland:
            "Bremen, Mecklenburg-Vorpommern, Niedersachsen, Nordrhein-Westfalen, Sachsen-Anhalt, Schleswig-Holstein",
          Adresse: "Zum Blauen See 7, 31275 Lehrte",
          "E-Mail": "info@bkkexklusiv.de",
        },
        {
          Name: "BKK Faber-Castell & Partner",
          Bundesland: "Bayern",
          Adresse: "Bahnhofstraße 45, 94209 Regen",
          "E-Mail": "regen@bkk-faber-castell.de",
        },
        {
          Name: "BKK firmus",
          Bundesland: "Bundesweit",
          Adresse: "Gottlieb-Daimler-Str. 11, 28237 Bremen",
          "E-Mail": "impressum@bkk-firmus.de",
        },
        {
          Name: "BKK Freudenberg",
          Bundesland:
            "Baden-Württemberg, Bayern, Berlin, Hessen, Niedersachsen, Nordrhein-Westfalen, Rheinland-Pfalz, Sachsen",
          Adresse: "Höhnerweg 2 – 4, 69469 Weinheim",
          "E-Mail": "bkk@bkk-freudenberg.de",
        },
        {
          Name: "BKK GILDEMEISTER SEIDENSTICKER",
          Bundesland: "Bundesweit",
          Adresse: "Winterstraße 49, 33649 Bielefeld",
          "E-Mail": "info@bkkgs.de",
        },
        {
          Name: "BKK Groz-Beckert",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Unter dem Malesfelsen 72, 72458 Albstadt",
          "E-Mail": "info@bkk-gb.de",
        },
        {
          Name: "BKK Herkules",
          Bundesland: "Bayern, Hessen, Niedersachsen",
          Adresse: "Jordanstraße 6, 34117 Kassel",
          "E-Mail": "info@bkk-herkules.de",
        },
        {
          Name: "BKK Linde",
          Bundesland: "Bundesweit",
          Adresse: "Konrad-Adenauer-Ring 33, 65187 Wiesbaden",
          "E-Mail": "info@bkk-linde.de",
        },
        {
          Name: "BKK MAHLE",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Pragstraße 26-46, 70376 Stuttgart",
          "E-Mail": "info@bkk-mahle.de",
        },
        {
          Name: "bkk melitta hmr",
          Bundesland:
            "Baden-Württemberg, Bayern, Brandenburg, Bremen, Hessen, Niedersachsen, Nordrhein-Westfalen, Sachsen, Sachsen-Anhalt, Schleswig-Holstein",
          Adresse: "Marienstr. 122, 32425 Minden",
          "E-Mail": "info@bkk-melitta.de",
        },
        {
          Name: "BKK Miele",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Carl-Miele-Straße 29, 33332 Gütersloh",
          "E-Mail": "info@bkk-miele.de",
        },
        {
          Name: "BKK MTU",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Hochstraße 40, 88045 Friedrichshafen",
          "E-Mail": "info@bkk-mtu.de",
        },
        {
          Name: "BKK PFAFF",
          Bundesland: "Rheinland-Pfalz",
          Adresse: "Pirmasenser Str. 132, 67655 Kaiserslautern",
          "E-Mail": "info@bkk-pfaff.de",
        },
        {
          Name: "BKK Pfalz",
          Bundesland: "Bundesweit",
          Adresse: "Lichtenberger-straße 16, 67059 Ludwigshafen",
          "E-Mail": "info@bkkpfalz.de",
        },
        {
          Name: "BKK ProVita",
          Bundesland: "Bundesweit",
          Adresse: "Rotkreuzplatz 8, 80634 München",
          "E-Mail": "info@bkk-provita.de",
        },
        {
          Name: "BKK Public",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Thiestr. 15, 38226 Salzgitter",
          "E-Mail": "service@bkk-public.de",
        },
        {
          Name: "BKK Rieker.RICOSTA.Weisser",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Gänsäcker 3, 78532 Tuttlingen",
          "E-Mail": "info@bkk-rrw.de",
        },
        {
          Name: "BKK Salzgitter",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Thiestr. 15, 38226 Salzgitter",
          "E-Mail": "service@bkk-salzgitter.de",
        },
        {
          Name: "BKK Scheufelen",
          Bundesland: "Baden-Württemberg",
          Adresse: "Schöllkopfstraße 65, 73230 Kirchheim/Teck",
          "E-Mail": "info@bkk-scheufelen.de",
        },
        {
          Name: "BKK Schwarzwald-Baar-Heuberg",
          Bundesland: "Baden-Württemberg",
          Adresse: "Löhrstr. 45, 78647 Trossingen",
          "E-Mail": "info@bkk-sbh.de",
        },
        {
          Name: "BKK Technoform",
          Bundesland:
            "Baden-Württemberg, Bayern, Niedersachsen, Rheinland-Pfalz",
          Adresse: "August-Spindler-Straße 1, 37079 Göttingen",
          "E-Mail": "Willkommen@bkk-technoform.de",
        },
        {
          Name: "BKK Textilgruppe Hof",
          Bundesland: "Bayern",
          Adresse: "Fabrikzeile 21, 95028 Hof",
          "E-Mail": "info@BKK-Textilgruppe-Hof.de",
        },
        {
          Name: "BKK VDN",
          Bundesland: "Hamburg, Nordrhein-Westfalen, Sachsen",
          Adresse: "Rosenweg 15, 58239 Schwerte",
          "E-Mail": "info@bkk-vdn.de",
        },
        {
          Name: "BKK VerbundPlus",
          Bundesland: "Bundesweit",
          Adresse: "Zeppelinring 13, 88400 Biberach",
          "E-Mail": "info@bkkvp.de",
        },
        {
          Name: "BKK VBU",
          Bundesland: "Bundesweit",
          Adresse: "Lindenstraße 67, 10969 Berlin",
          "E-Mail": "info@bkk-vbu.de",
        },
        {
          Name: "BKK Voralb HELLER*INDEX*LEUZE",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Gebrüder-Heller-Straße 15, 72622 Nürtingen",
          "E-Mail": "info@bkk-voralb.de",
        },
        {
          Name: "BKK Werra-Meissner",
          Bundesland: "Bayern, Hessen",
          Adresse: "Straßburger Straße 5, 37269 Eschwege",
          "E-Mail": "info@bkk-wm.de",
        },
        {
          Name: "BKK Wirtschaft & Finanzen",
          Bundesland:
            "Baden-Württemberg, Bayern, Berlin, Brandenburg, Bremen, Hamburg, Hessen, Niedersachsen, Nordrhein-Westfalen, Rheinland-Pfalz, Saarland, Sachsen",
          Adresse: "Bahnhofstraße 19, 34212 Melsungen",
          "E-Mail": "info@bkk-wf.de",
        },
        {
          Name: "BKK Würth",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Gartenstraße 11, 74653 Künzelsau",
          "E-Mail": "info@bkk-wuerth.de",
        },
        {
          Name: "BKK ZF & Partner",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Casinostraße 47, 56068 Koblenz",
          "E-Mail": "koblenz@bkk-zf-partner.de",
        },
        {
          Name: "BKK_DürkoppAdler",
          Bundesland: "Nordrhein-Westfalen",
          Adresse: "Stieghorster Str. 66, 33605 Bielefeld",
          "E-Mail": "info@bkk-da.de",
        },
        {
          Name: "BKK24",
          Bundesland: "Bundesweit",
          Adresse: "Sülbecker Brand 1, 31683 Obernkirchen",
          "E-Mail": "info@bkk24.de",
        },
        {
          Name: "BMW BKK",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Mengkofener Str. 6, 84130 Dingolfing",
          "E-Mail": "Informationen@bmwbkk.de",
        },
        {
          Name: "Bosch BKK",
          Bundesland:
            "Baden-Württemberg, Bayern, Berlin, Brandenburg, Hamburg, Hessen, Mecklenburg-Vorpommern, Niedersachsen, Nordrhein-Westfalen, Rheinland-Pfalz, Saarland, Sachsen, Sachsen-Anhalt, Thüringen",
          Adresse: "Kruppstr. 19, 70469 Stuttgart",
          "E-Mail": "info@Bosch-BKK.de",
        },
        {
          Name: "Continentale Betriebskrankenkasse",
          Bundesland: "Bundesweit",
          Adresse: "Sengelmannstr. 120, 22335 Hamburg",
          "E-Mail": "kundenservice@continentale-bkk.de",
        },
        {
          Name: "DAK-Gesundheit",
          Bundesland: "Bundesweit",
          Adresse: "Nagelsweg 27 - 31, 20097 Hamburg",
          "E-Mail": "service@dak.de",
        },
        {
          Name: "Debeka BKK",
          Bundesland: "Bundesweit",
          Adresse: "Im Metternicher Feld 50, 56072 Koblenz",
          "E-Mail": "info@debeka-bkk.de",
        },
        {
          Name: "energie-Betriebskrankenkasse",
          Bundesland: "Bundesweit",
          Adresse: "30134 Hannover",
          "E-Mail": "info@energie-bkk.de",
        },
        {
          Name: "Ernst & Young BKK",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Arnulfstr. 59, 80636 München",
          "E-Mail": "info@ey-bkk.de",
        },
        {
          Name: "hkk",
          Bundesland: "Bundesweit",
          Adresse: "Martinistraße 26, 28195 Bremen",
          "E-Mail": "info@hkk.de",
        },
        {
          Name: "Heimat Krankenkasse",
          Bundesland: "Bundesweit",
          Adresse: "Herforder Straße 23, 33602 Bielefeld",
          "E-Mail": "info@heimat-krankenkasse.de",
        },
        {
          Name: "HEK - Hanseatische Krankenkasse",
          Bundesland: "Bundesweit",
          Adresse: "Wandsbeker Zollstraße 86 - 90, 22041 Hamburg",
          "E-Mail": "kontakt@hek.de",
        },
        {
          Name: "IKK - Die Innovationskasse",
          Bundesland: "Bundesweit",
          Adresse: "Lachswehrallee 1, 23558 Lübeck",
          "E-Mail": "mail@die-ik.de",
        },
        {
          Name: "IKK Brandenburg und Berlin",
          Bundesland: "Berlin, Brandenburg",
          Adresse: "Ziolkowskistraße 6, 14480 Potsdam",
          "E-Mail": "service@ikkbb.de",
        },
        {
          Name: "IKK classic",
          Bundesland: "Bundesweit",
          Adresse: "Tannenstraße 4 b, 01099 Dresden",
          "E-Mail": "info@ikk-classic.de",
        },
        {
          Name: "IKK gesund plus",
          Bundesland: "Bundesweit",
          Adresse: "Umfassungsstraße 85, 39124 Magdeburg",
          "E-Mail": "info@ikk-gesundplus.de",
        },
        {
          Name: "IKK Südwest",
          Bundesland: "Hessen, Rheinland-Pfalz, Saarland",
          Adresse: "Europaallee 3 – 4, 66113 Saarbrücken",
          "E-Mail": "info@ikk-sw.de",
        },
        {
          Name: "KARL MAYER BKK",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Industriestraße 3, 63179 Obertshausen",
          "E-Mail": "info@karlmayer-bkk.de",
        },
        {
          Name: "Kaufmännische Krankenkasse – KKH",
          Bundesland: "Bundesweit",
          Adresse: "Karl-Wiechert-Allee 61, 30625 Hannover",
          "E-Mail": "service@kkh.de",
        },
        {
          Name: "KNAPPSCHAFT",
          Bundesland: "Bundesweit",
          Adresse: "Pieperstraße 14-28, 44789 Bochum",
          "E-Mail": "zentrale@kbs.de",
        },
        {
          Name: "Koenig & Bauer BKK",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Friedrich-Koenig-Straße 4, 97080 Würzburg",
          "E-Mail": "info@koenig-bauer-bkk.de",
        },
        {
          Name: "Krones BKK",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Bayerwaldstraße 2L, 93073 Neutraubling",
          "E-Mail": "bkk.info@krones.com",
        },
        {
          Name: "Mercedes-Benz BKK",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Mercedesstr. 120, 70372 Stuttgart",
          "E-Mail": "Je nach Region: z.b. Berlin@mercedes-benz-bkk.com",
        },
        {
          Name: "Merck BKK",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Frankfurter Str. 129, 64293 Darmstadt",
          "E-Mail": "bkk@merckgroup.com",
        },
        {
          Name: "mhplus Betriebskrankenkasse",
          Bundesland:
            "Baden-Württemberg, Bayern, Berlin, Hamburg, Hessen, Mecklenburg-Vorpommern, Niedersachsen, Nordrhein-Westfalen, Rheinland-Pfalz, Sachsen, Sachsen-Anhalt, Schleswig-Holstein, Thüringen",
          Adresse: "Franckstrasse 8, 71636 Ludwigsburg",
          "E-Mail": "info@mhplus.de",
        },
        {
          Name: "Novitas BKK",
          Bundesland: "Bundesweit",
          Adresse: "Schifferstraße 92-100, 47059 Duisburg",
          "E-Mail": "info@novitas-bkk.de",
        },
        {
          Name: "Pronova BKK",
          Bundesland: "Bundesweit",
          Adresse: "Rheinallee 13, 67061 Ludwigshafen",
          "E-Mail": "service@pronovabkk.de",
        },
        {
          Name: "R+V Betriebskrankenkasse",
          Bundesland: "Bundesweit",
          Adresse: "Kreuzberger Ring 21, 65205 Wiesbaden",
          "E-Mail": "info@ruv-bkk.de",
        },
        {
          Name: "Salus BKK",
          Bundesland: "Bundesweit",
          Adresse: "Züricher Straße 27, 81476 München",
          "E-Mail": "service@salus-bkk.de",
        },
        {
          Name: "SECURVITA BKK",
          Bundesland:
            "Baden-Württemberg, Bayern, Berlin, Brandenburg, Hamburg, Hessen, Mecklenburg-Vorpommern, Niedersachsen, Nordrhein-Westfalen, Rheinland-Pfalz, Sachsen, Sachsen-Anhalt, Schleswig-Holstein, Thüringen",
          Adresse: "Lübeckertordamm 1-3, 20099 Hamburg",
          "E-Mail": "mail@securvita.de",
        },
        {
          Name: "SBK",
          Bundesland: "Bundesweit",
          Adresse: "St.-Pöltener-Str. 37, 89522 Heidenheim",
          "E-Mail": "info@sbk.org",
        },
        {
          Name: "SKD BKK",
          Bundesland:
            "Baden-Württemberg, Bayern, Berlin, Bremen, Hamburg, Hessen, Niedersachsen, Nordrhein-Westfalen, Saarland, Sachsen, Schleswig-Holstein",
          Adresse: "Schultesstraße 19 A, 97421 Schweinfurt",
          "E-Mail": "service@skd-bkk.de",
        },
        {
          Name: "SVLFG",
          Bundesland: "Für Arbeitende in der Landwirtschaft",
          Adresse: "Weißensteinstr. 70 - 72, 34131 Kassel",
          "E-Mail": "Kontaktformular",
        },
        {
          Name: "Südzucker BKK",
          Bundesland: "Nur für Mitarbeitende",
          Adresse: "Joseph-Meyer-Str. 13-15, 68167 Mannheim",
          "E-Mail": "info@suedzucker-bkk.de",
        },
        {
          Name: "Techniker Krankenkasse",
          Bundesland: "Bundesweit",
          Adresse: "Bramfelder Straße 140, 22305 Hamburg",
          "E-Mail": "service@tk.de",
        },
        {
          Name: "TUI BKK",
          Bundesland: "Bundesweit",
          Adresse: "Karl-Wiechert-Allee 23, 30625 Hannover",
          "E-Mail": "service@tui-bkk.de",
        },
        {
          Name: "VIACTIV",
          Bundesland: "Bundesweit",
          Adresse: "Universitätsstraße 43, 44789 Bochum",
          "E-Mail": "service@viactiv.de",
        },
        {
          Name: "vivida bkk",
          Bundesland: "Bundesweit",
          Adresse: "Spittelstraße 50, 78056 Villingen-Schwenningen",
          "E-Mail": "Info@vividabkk.de",
        },
        {
          Name: "WMF Betriebskrankenkasse",
          Bundesland: "Bundesweit",
          Adresse: "Fabrikstraße 48, 73312 Geislingen",
          "E-Mail": "service@wmf-bkk.de",
        },
        {
          Name: "Allianz Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Königinstraße 28, 80802 München",
          "E-Mail": "info@allianz.de",
        },
        {
          Name: "Alte Oldenburger Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Alte-Oldenburger-Platz 1, 49377 Vechta",
          "E-Mail": "info@alte-oldenburger.de",
        },
        {
          Name: "ARAG Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Hollerithstraße 11, 81829 München",
          "E-Mail": "service@ARAG.de",
        },
        {
          Name: "AXA Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Colonia-Allee 10-20, 51067 Köln",
          "E-Mail": "service@axa.de",
        },
        {
          Name: "Barmenia Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Barmenia-Allee 1, 42119 Wuppertal",
          "E-Mail": "info@barmenia.de",
        },
        {
          Name: "Concordia Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Karl-Wiechert-Allee 55, 30625 Hannover",
          "E-Mail": "versicherungen@concordia.de",
        },
        {
          Name: "Continentale Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Ruhrallee 92, 44139 Dortmund",
          "E-Mail": "info@continentale.de",
        },
        {
          Name: "Debeka Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Ferdinand-Sauerbruch-Straße 18, 56073 Koblenz",
          "E-Mail": "kundenservice@debeka.de",
        },
        {
          Name: "Deutscher Ring Krankenversicherung",
          Bundesland: "-",
          Adresse: "-",
          "E-Mail": "-",
        },
        {
          Name: "DEVK Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Riehler Straße 190, 50735 Köln",
          "E-Mail": "info@devk.de",
        },
        {
          Name: "DKV Deutsche Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Aachener Straße 300, 50933 Köln",
          "E-Mail": "service@dkv.com",
        },
        {
          Name: "FREIE ARZT-UND MEDIZINKASSE",
          Bundesland: "Hessen",
          Adresse: "Hansaallee 154, 60320 Frankfurt am Main",
          "E-Mail": "info@famk.de",
        },
        {
          Name: "Generali Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Adenauerring 7, 81737 München",
          "E-Mail": "gesundheit@generali.com",
        },
        {
          Name: "Gothaer Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Arnoldiplatz 1, 50969 Köln",
          "E-Mail": "info@gothaer.de",
        },
        {
          Name: "HanseMerkur",
          Bundesland: "Bundesweit",
          Adresse: "Siegfried-Wedells-Platz 1, 20354 Hamburg",
          "E-Mail": "info@hansemerkur.de",
        },
        {
          Name: "HUK-Coburg Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Bahnhofsplatz, 96450 Coburg",
          "E-Mail": "Info@HUK-COBURG.de",
        },
        {
          Name: "Inter Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Erzbergerstr. 9-15, 68165 Mannheim",
          "E-Mail": "info@inter.de",
        },
        {
          Name: "KUK Berufsfeuerwehr Hannover",
          Bundesland:
            "Nur für Angehörige von Berufsfeuerwehren, Feuerwachen und Polizei",
          Adresse: "Karl-Wiechert-Allee 60 B, 30625 Hannover",
          "E-Mail": "info@kuk-hannover.de",
        },
        {
          Name: "Krankenversorgung der Bundesbahnbeamten",
          Bundesland: "Nur für Beamtinnen und Beamten des BEV",
          Adresse: "Salvador-Allende-Straße 7, 60487 Frankfurt am Main",
          "E-Mail": "info@kvb.bund.de",
        },
        {
          Name: "LIGA Krankenversicherung katholischer Priester",
          Bundesland: "Nur für katholische Priester",
          Adresse: "Weißenburgstraße 17, 93055 Regensburg",
          "E-Mail": "service@ligakranken.de",
        },
        {
          Name: "LKH Landeskrankenhilfe",
          Bundesland: "Bundesweit",
          Adresse: "Uelzener Straße 120, 21335 Lüneburg",
          "E-Mail": "info@lkh.de",
        },
        {
          Name: "LVM Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Kolde-Ring 21, 48151 Münster",
          "E-Mail": "info@lvm.de",
        },
        {
          Name: "Mannheimer Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Augustaanlage 66, 68165 Mannheim",
          "E-Mail": "service@mannheimer.de",
        },
        {
          Name: "Mecklenburgische Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Platz der Mecklenburgischen 1, 30625 Hannover",
          "E-Mail": "service@mecklenburgische.de",
        },
        {
          Name: "Münchener Verein Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Pettenkoferstraße 19, 80336 München",
          "E-Mail": "info@muenchener-verein.de",
        },
        {
          Name: "Nürnberger Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Ostendstraße 100, 90334 Nürnberg",
          "E-Mail": "info@nuernberger.de",
        },
        {
          Name: "Ottonova Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Ottostraße 4, 80333 München",
          "E-Mail": "helpdesk@ottonova.de",
        },
        {
          Name: "Postbeamtenkrankenkasse",
          Bundesland: "Keine Aufnahme neuer Mitglieder",
          Adresse: "Nauheimer Straße 98, 70372 Stuttgart",
          "E-Mail": "service@pbeakk.de",
        },
        {
          Name: "Provinzial",
          Bundesland: "Bundesweit",
          Adresse: "Sophienblatt 33, 24114 Kiel",
          "E-Mail": "service@provinzial.de",
        },
        {
          Name: "R + V Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Raiffeisenplatz 1, 65189 Wiesbaden",
          "E-Mail": "Kontaktformular",
        },
        {
          Name: "Signal Iduna",
          Bundesland: "Bundesweit",
          Adresse: "Joseph-Scherer-Straße 3, 44139 Dortmund",
          "E-Mail": "info@signal-iduna.de",
        },
        {
          Name: "Süddeutsche Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Raiffeisenplatz 11, 70736 Fellbach",
          "E-Mail": "sdk@sdk.de",
        },
        {
          Name: "UKV Union Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Peter-Zimmer-Str. 2, 66123 Saarbrücken",
          "E-Mail": "service@ukv.de",
        },
        {
          Name: "Universa Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Sulzbacher Straße 1-7, 90489 Nürnberg",
          "E-Mail": "info@uniVersa.de",
        },
        {
          Name: "Versicherungskammer Bayern",
          Bundesland: "Bundesweit",
          Adresse: "Maximilianstraße 53, 80530 München",
          "E-Mail": "service@vkb.de",
        },
        {
          Name: "VGH Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "Schiffgraben 4, 30159 Hannover",
          "E-Mail": "service@vgh.de",
        },
        {
          Name: "Württembergische Krankenversicherung",
          Bundesland: "Bundesweit",
          Adresse: "W&W-Platz 1, 70806 Kornwestheim",
          "E-Mail": "info@wuerttembergische.de",
        },
      ],
      selectedKasse: null,
      ChangeDateFormat: false,
      signingpad: "",
      PflegeversicherungAdresse: "",
    };
  },
};
</script>
